import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { PAGINATION_INITIAL_STATE } from "./constants"

// ACTIONS - List
const requested = createAction("crewProfiles/requested")
const received = createAction("crewProfiles/received")
const failed = createAction("crewProfiles/failed")
const cleaned = createAction("crewProfiles/cleaned")

// ACTIONS - Single Profile
const profileRequested = createAction("crewProfiles/profile/requested")
const profileReceived = createAction("crewProfiles/profile/received")
const profileFailed = createAction("crewProfiles/profile/failed")

// ACTIONS - Update Profile
const updateRequested = createAction("crewProfiles/update/requested")
const updateReceived = createAction("crewProfiles/update/received")
const updateFailed = createAction("crewProfiles/update/failed")

// REDUCER
const initialState = {
  ...LOADING_INITIAL_STATE,
  items: [],
  metadata: PAGINATION_INITIAL_STATE,
  // Single profile state
  profile: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  // Update state
  update: {
    ...LOADING_INITIAL_STATE
  }
}

const crewProfilesReducer = createReducer(initialState, {
  // List actions
  [requested.type]: (state) => {
    setRequested(state)
  },
  [received.type]: (state, { payload }) => {
    setReceived(state)
    state.items = payload.data
    Object.assign(state.metadata, payload.metadata)
  },
  [failed.type]: (state) => {
    setFailed(state)
  },
  [cleaned.type]: () => initialState,

  // Single profile actions
  [profileRequested.type]: (state) => {
    setRequested(state.profile)
  },
  [profileReceived.type]: (state, { payload }) => {
    setReceived(state.profile)
    state.profile.data = payload.data
  },
  [profileFailed.type]: (state) => {
    setFailed(state.profile)
  },

  // Update profile actions
  [updateRequested.type]: (state) => {
    setRequested(state.update)
  },
  [updateReceived.type]: (state, { payload }) => {
    setReceived(state.update)
    state.profile.data = payload.data
  },
  [updateFailed.type]: (state) => {
    setFailed(state.update)
  }
})

export default crewProfilesReducer

// Helper to map frontend status to backend status
const mapStatusToBackend = (status) => {
  if (!status || status === "all") return "all"

  const statusMap = {
    available: "active",
    disabled: "deactivated"
  }

  return statusMap[status] || status
}

// API ACTIONS
export const getCrewProfiles = (params = {}) => {
  const { status, roleIds, search, page = 1, per_page = 10, sort_by, sort_direction, available_on, ...otherParams } = params

  // Build base URL params
  const urlParams = new URLSearchParams()

  // Add pagination
  urlParams.append('page', page)
  urlParams.append('per_page', per_page)

  // Add status filter (map frontend to backend)
  if (status && status !== "all") {
    urlParams.append('status', mapStatusToBackend(status))
  }

  // Add role filter - append each role ID separately
  if (roleIds && roleIds.length > 0) {
    roleIds.forEach(roleId => {
      urlParams.append('role_ids[]', roleId)
    })
  }

  // Add search filter
  if (search && search.trim()) {
    urlParams.append('search', search.trim())
  }

  // Add sorting parameters
  if (sort_by) {
    urlParams.append('sort_by', sort_by)
    urlParams.append('sort_direction', sort_direction || 'asc')
  }

  // Add available_on date filter (YYYY-MM-DD format)
  if (available_on) {
    urlParams.append('available_on', available_on)
  }

  // Add any other params
  Object.entries(otherParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      urlParams.append(key, value)
    }
  })

  return api({
    url: `/guide/profiles?${urlParams.toString()}`,
    method: "get",
    onStart: requested.type,
    onSuccess: received.type,
    onError: failed.type
  })
}

export const cleanCrewProfiles = () => (dispatch) => dispatch(cleaned())

// Get single crew profile
export const getCrewProfile = (id) =>
  api({
    url: `/guide/profiles/${id}`,
    method: "get",
    onStart: profileRequested.type,
    onSuccess: profileReceived.type,
    onError: profileFailed.type
  })

// Update crew profile
export const updateCrewProfile = (id, formData) =>
  api({
    url: `/guide/profiles/${id}`,
    method: "put",
    data: formData,
    onStart: updateRequested.type,
    onSuccess: updateReceived.type,
    onError: updateFailed.type
  })
